<template>
  <b-modal
    id="app-download-modal"
    ref="appDownloadModal"
    v-model="showModal"
    title="Download Applications"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
    no-close-on-esc
  >
    <form @submit.prevent="downloadApplications">
      <!-- Status Selection -->
      <div class="mb-4">
        <label class="form-label fw-bold">
          <b-icon icon="funnel" class="mr-2"></b-icon>
          Select Application Status:
        </label>
        <b-form-checkbox-group
          v-model="selectedStatuses"
          :options="statusOptions"
          class="status-checkbox-group"
          stacked
        />
        <small class="text-muted">
          Select one or more statuses to include in the download
        </small>
      </div>

      <!-- Date Range Filter (Optional) -->
      <div class="mb-4">
        <label class="form-label fw-bold">
          <b-icon icon="calendar-range" class="mr-2"></b-icon>
          Date Range (Optional):
        </label>
        <div class="row">
          <div class="col-md-6">
            <label class="form-label">From Date:</label>
            <b-form-input
              v-model="dateRange.from"
              type="date"
              placeholder="Start date"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label">To Date:</label>
            <b-form-input
              v-model="dateRange.to"
              type="date"
              placeholder="End date"
            />
          </div>
        </div>
      </div>

      <!-- File Format Selection -->
      <div class="mb-4">
        <label class="form-label fw-bold">
          <b-icon icon="file-earmark" class="mr-2"></b-icon>
          Download Format:
        </label>
        <b-form-radio-group
          v-model="downloadFormat"
          :options="formatOptions"
          class="format-radio-group"
        />
      </div>

      <!-- Additional Filters -->
      <div class="mb-4">
        <b-card class="bg-light">
          <b-card-title class="h6">
            <b-icon icon="sliders" class="mr-2"></b-icon>
            Additional Filters
          </b-card-title>

          <!-- Session Filter -->
          <div class="mb-3">
            <label class="form-label">Academic Session:</label>
            <b-form-select v-model="filters.session" :options="sessionOptions">
              <template #first>
                <b-form-select-option value=""
                  >All Sessions</b-form-select-option
                >
              </template>
            </b-form-select>
          </div>

          <!-- Programme Filter -->
          <!-- <div class="mb-3">
            <label class="form-label">Programme:</label>
            <b-form-select
              v-model="filters.programme"
              :options="programmeOptions"
            >
              <template #first>
                <b-form-select-option value=""
                  >All Programmes</b-form-select-option
                >
              </template>
            </b-form-select>
          </div> -->

          <!-- Department Filter -->
          <div
            class="mb-0"
            v-if="roleInUse === 'admin' || roleInUse === 'SUPERADMIN'"
          >
            <label class="form-label">Department:</label>
            <b-form-select
              v-model="filters.department"
              :options="departmentOptions"
            >
              <template #first>
                <b-form-select-option value=""
                  >All Departments
                </b-form-select-option>
              </template>
            </b-form-select>
          </div>
        </b-card>
      </div>

      <!-- Download Preview -->
      <div class="mb-4" v-if="selectedStatuses.length > 0">
        <b-card class="border-info">
          <b-card-title class="h6 text-info">
            <b-icon icon="info-circle" class="mr-2"></b-icon>
            Download Preview
          </b-card-title>
          <ul class="mb-0">
            <li v-for="status in selectedStatuses" :key="status" class="mb-1">
              <b-badge :variant="getStatusVariant(status)" class="mr-2">
                {{ getStatusDisplayName(status) }}
              </b-badge>
              <small class="text-muted">
                {{ getStatusCount(status) }} applications
              </small>
            </li>
          </ul>
          <hr />
          <p class="mb-0 fw-bold">
            Total: {{ totalApplicationsCount }} applications
          </p>
        </b-card>
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <button
            type="button"
            class="btn btn-outline-secondary me-2"
            @click="selectAllStatuses"
            :disabled="isLoading"
          >
            <b-icon icon="check-all" class="mr-1"></b-icon>
            Select All
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="clearAllStatuses"
            :disabled="isLoading"
          >
            <b-icon icon="x-circle" class="mr-1"></b-icon>
            Clear All
          </button>
        </div>

        <div>
          <button
            type="button"
            class="btn btn-outline-danger me-2"
            @click="closeModal"
            :disabled="isLoading"
          >
            Cancel
          </button>

          <button
            type="submit"
            class="btn btn-primary"
            :disabled="isLoading || selectedStatuses.length === 0"
          >
            <b-spinner small v-if="isLoading" class="mr-1"></b-spinner>
            <b-icon v-else icon="download" class="mr-1"></b-icon>
            {{ isLoading ? "Preparing Download..." : "Download Applications" }}
          </button>
        </div>
      </div>
    </form>

    <!-- Progress Bar -->
    <div v-if="isLoading" class="mt-3">
      <b-progress :value="downloadProgress" :max="100" animated striped>
        <span>{{ downloadProgress }}%</span>
      </b-progress>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "AppDownloadModal",
  props: {
    // API endpoint for downloading
    downloadEndpoint: {
      type: String,
      default: "api/applications/download",
    },
    // Available sessions
    sessions: {
      type: Array,
      default: () => [],
    },
    // Available programmes
    programmes: {
      type: Array,
      default: () => [],
    },
    // Available departments
    departments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showModal: false,
      isLoading: false,
      downloadProgress: 0,
      selectedStatuses: ["submitted under processing"],
      downloadFormat: "excel",
      dateRange: {
        from: "",
        to: "",
      },
      filters: {
        session: "",
        programme: "",
        department: "",
      },
      statusCounts: {},
      statusOptions: [
        {
          text: "Submitted Under Processing",
          value: "submitted under processing",
        },
        {
          text: "Awaiting Submission",
          value: "awaiting submission",
        },
        {
          text: "Denied",
          value: "denied",
        },
        {
          text: "Approved",
          value: "approved",
        },
        {
          text: "Student",
          value: "student",
        },
        {
          text: "Pending",
          value: "pending",
        },
        {
          text: "Recommended for Admission",
          value: "recommended for admission",
        },
        {
          text: "Recommendation Queried",
          value: "recommendation queried",
        },
      ],
      formatOptions: [
        {
          text: "Excel Spreadsheet (.xlsx)",
          value: "excel",
        },
        {
          text: "CSV File (.csv)",
          value: "csv",
        },
        {
          text: "PDF Report (.pdf)",
          value: "pdf",
        },
      ],
    };
  },
  computed: {
    roleInUse() {
      return this.$store.getters["getUser"]?.roleInUse;
    },
    sessionOptions() {
      const uniqueSessions = [];
      const seenSessionNames = new Set();

      this.sessions.forEach((session) => {
        if (!seenSessionNames.has(session.session_name)) {
          seenSessionNames.add(session.session_name);
          uniqueSessions.push({
            value: session.id,
            text: session.session_name,
          });
        }
      });

      return uniqueSessions;
    },
    programmeOptions() {
      return this.programmes.map((programme) => ({
        value: programme.id,
        text: programme.programme,
      }));
    },
    departmentOptions() {
      return this.departments
        .map((department) => ({
          value: department.id,
          text: department.department,
        }))
        .sort((a, b) => a.text.localeCompare(b.text)); // Sort alphabetically
    },

    totalApplicationsCount() {
      return this.selectedStatuses.reduce((total, status) => {
        return total + (this.statusCounts[status] || 0);
      }, 0);
    },
  },
  methods: {
    // Open modal
    openModal(options = {}) {
      this.resetForm();

      // Pre-select statuses if provided, otherwise keep default
      if (options.statuses && options.statuses.length > 0) {
        this.selectedStatuses = options.statuses;
      }
      // If no statuses provided, keep the default "submitted under processing"

      // Set filters if provided
      if (options.filters) {
        this.filters = { ...this.filters, ...options.filters };
      }

      this.loadStatusCounts();
      this.showModal = true;
    },

    // Close modal
    closeModal() {
      this.showModal = false;
      this.resetForm();
    },

    // Reset form
    resetForm() {
      this.selectedStatuses = ["submitted under processing"];
      this.downloadFormat = "excel";
      this.dateRange = { from: "", to: "" };
      this.filters = { session: "", programme: "", department: "" };
      this.isLoading = false;
      this.downloadProgress = 0;
      this.statusCounts = {};
    },

    // Select all statuses
    selectAllStatuses() {
      this.selectedStatuses = this.statusOptions.map((option) => option.value);
    },

    // Clear all statuses
    clearAllStatuses() {
      this.selectedStatuses = [];
    },

    // Load status counts from backend
    async loadStatusCounts() {
      try {
        const response = await this.$store.dispatch(
          "Applications/getStatusCounts",
          {
            session: this.filters.session,
            programme: this.filters.programme,
            department: this.filters.department,
          }
        );

        this.statusCounts = response?.data || {};
      } catch (error) {
        console.error("Failed to load status counts:", error);
      }
    },

    // Get status count
    getStatusCount(status) {
      return this.statusCounts[status] || 0;
    },

    // Get status display name
    getStatusDisplayName(status) {
      const option = this.statusOptions.find((opt) => opt.value === status);
      return option ? option.text : status;
    },

    // Get badge variant for status
    getStatusVariant(status) {
      const variants = {
        "submitted under processing": "warning",
        "awaiting submission": "secondary",
        denied: "danger",
        approved: "success",
        student: "info",
        pending: "warning",
        "recommended for admission": "primary",
        "recommendation queried": "danger",
      };

      return variants[status] || "secondary";
    },

    // Download applications

    async downloadApplications() {
      if (this.selectedStatuses.length === 0) {
        Toast.fire({
          icon: "warning",
          title: "Please select at least one status",
        });
        return;
      }

      this.isLoading = true;
      this.downloadProgress = 0;

      try {
        const downloadData = {
          statuses: this.selectedStatuses,
          format: this.downloadFormat,
          date_range: JSON.stringify({
            from: this.dateRange.from,
            to: this.dateRange.to,
          }),
          filters: JSON.stringify({
            session: this.filters.session,
            programme: this.filters.programme,
            department: this.filters.department,
          }),
        };

        console.log("Download data:", downloadData);

        // Simulate progress
        const progressInterval = setInterval(() => {
          if (this.downloadProgress < 90) {
            this.downloadProgress += 10;
          }
        }, 200);

        const response = await this.$store.dispatch(
          "Application/downloadFilterApplications",
          downloadData
        );

        clearInterval(progressInterval);
        this.downloadProgress = 100;

        // Handle file download (blob response)
        this.downloadFile(response, this.getFileName());

        Toast.fire({
          icon: "success",
          title: "Applications downloaded successfully!",
        });

        this.$emit("download-completed", {
          statuses: this.selectedStatuses,
          format: this.downloadFormat,
          count: this.totalApplicationsCount,
          timestamp: new Date(),
        });

        this.closeModal();
      } catch (error) {
        console.error("Download error:", error);

        // Enhanced error handling
        let errorMessage = "Failed to download applications";

        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = "No applications found matching your criteria";
          } else if (error.response.status === 500) {
            errorMessage = "Server error occurred during download";
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        Toast.fire({
          icon: "error",
          title: errorMessage,
        });
      } finally {
        this.isLoading = false;
        this.downloadProgress = 0;
      }
    },

    // Helper method for file downloads
    downloadFile(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    // Simplified filename generator
    getFileName() {
      const timestamp = new Date().toISOString().slice(0, 10);
      const statusText =
        this.selectedStatuses.length === 1
          ? this.selectedStatuses[0].replace(/\s+/g, "_")
          : "multiple_statuses";

      const extension = this.downloadFormat === "excel" ? "xlsx" : "csv";

      return `applications_${statusText}_${timestamp}.${extension}`;
    },
  },
  created() {
    // Listen for download events
    Fire.$on("DOWNLOAD-APPLICATIONS", (data) => {
      this.openModal(data);
    });

    Fire.$on("DOWNLOAD-BY-STATUS", (status) => {
      this.openModal({
        statuses: [status],
      });
    });
  },
  beforeDestroy() {
    Fire.$off("DOWNLOAD-APPLICATIONS");
    Fire.$off("DOWNLOAD-BY-STATUS");
  },
};
</script>

<style scoped>
.status-checkbox-group {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem;
  background-color: #f8f9fa;
}

.format-radio-group {
  padding: 0.5rem;
}

.form-label {
  margin-bottom: 0.5rem;
  color: #495057;
  font-weight: 500;
}

.fw-bold {
  font-weight: 600 !important;
}

.me-2 {
  margin-right: 0.5rem !important;
}

/* Custom checkbox styling */
.status-checkbox-group .custom-control {
  margin-bottom: 0.5rem;
}

.status-checkbox-group .custom-control:last-child {
  margin-bottom: 0;
}

/* Progress bar styling */
.progress {
  height: 1.5rem;
}

/* Card styling */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

/* Badge styling */
.badge {
  font-size: 0.75rem;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .d-flex.justify-content-between {
    flex-direction: column;
  }

  .d-flex.justify-content-between > div {
    margin-bottom: 0.5rem;
  }

  .d-flex.justify-content-between > div:last-child {
    margin-bottom: 0;
  }
}
</style>
